import 'leaflet-imageoverlay-rotated'

import ycbcr from 'color-space/ycbcr'
import GeoTIFF, { fromBlob, GeoTIFFImage, Pool } from 'geotiff'
import L from 'leaflet'
import proj4 from 'proj4'
import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import { useIsMounted } from 'usehooks-ts'

import { loadEPSGDefinition } from '@/services/load-epsg-definition'

export class GeoTiffError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GeoTiffError'
  }
}

export interface GeoTiffProps {
  file?: File
  onReady?: (ready?: boolean) => void
  onError?: () => void
  isShow?: boolean
}

export function GeoTiff({ file, onReady, onError, isShow }: GeoTiffProps) {
  const map = useMap()
  const isMounted = useIsMounted()
  const layer = useRef<L.ImageOverlay | null>(null)

  function reproject(
    srcCRS: string,
    targetCRS: string,
    coordinates: [number, number],
  ): [number, number] {
    const [x, y] = proj4(srcCRS, targetCRS, coordinates)
    return [y, x]
  }

  async function getCornerPoints(image: GeoTIFFImage) {
    const [minX, minY, maxX, maxY] = image.getBoundingBox()

    const crsOptions = {
      projected: image.geoKeys?.ProjectedCSTypeGeoKey,
      geographic: image.geoKeys?.GeographicTypeGeoKey,
    }
    const selectedCRS = crsOptions.projected || crsOptions.geographic

    if (!selectedCRS) {
      throw new GeoTiffError('Nenhum CRS identificado no GeoTIFF')
    }

    await loadEPSGDefinition(selectedCRS)

    const srcCRS = `EPSG:${selectedCRS}`
    const targetCRS = 'EPSG:4326'

    const projectedCorners = {
      bottomLeft: reproject(srcCRS, targetCRS, [minX, minY]),
      bottomRight: reproject(srcCRS, targetCRS, [maxX, minY]),
      topRight: reproject(srcCRS, targetCRS, [maxX, maxY]),
      topLeft: reproject(srcCRS, targetCRS, [minX, maxY]),
    }

    return projectedCorners
  }

  async function getOverviewImage(tiff: GeoTIFF) {
    const DIMESION_LIMIT = 4096
    const imageCount = await tiff.getImageCount()
    let bestOverview: GeoTIFFImage | null = null

    for (let i = imageCount - 1; i >= 0; i--) {
      const overview = await tiff.getImage(i)
      const width = overview.getWidth()
      const height = overview.getHeight()
      const largestDimension = Math.max(width, height)

      if (largestDimension > DIMESION_LIMIT) {
        break
      }

      bestOverview = overview
    }

    if (!bestOverview) {
      throw new GeoTiffError('Nenhuma imagem do GeoTIFF pode ser exibida.')
    }

    return bestOverview
  }

  async function drawImage(
    overview: GeoTIFFImage,
    isYCBCR: boolean,
    samplesPerPixel: number,
  ) {
    const canvas = document.createElement('canvas')

    canvas.width = overview.getWidth()
    canvas.height = overview.getHeight()
    const ctx = canvas.getContext('2d')!
    const raster = await overview.readRasters({
      interleave: true,
      pool: new Pool(8),
    })

    const imageData = ctx.createImageData(canvas.width, canvas.height)

    const samplesPerPixelOptions: Record<number, () => void> = {
      3: () => {
        for (let i = 0, j = 0; i < raster.length; i += 3, j += 4) {
          let r = raster[i] as number
          let g = raster[i + 1] as number
          let b = raster[i + 2] as number

          if (isYCBCR) {
            const y = raster[i] as number
            const cb = raster[i + 1] as number
            const cr = raster[i + 2] as number

            ;[r, g, b] = ycbcr.rgb([y, cb, cr])
          }

          imageData.data[j] = Math.min(255, Math.max(0, r))
          imageData.data[j + 1] = Math.min(255, Math.max(0, g))
          imageData.data[j + 2] = Math.min(255, Math.max(0, b))
          imageData.data[j + 3] = 255
        }
      },
      4: () => {
        imageData.data.set(raster as [])
      },
    }

    const imageGenerator = samplesPerPixelOptions[samplesPerPixel]

    if (!imageGenerator)
      throw new GeoTiffError(
        `Número de bandas não suportado: ${samplesPerPixel}`,
      )

    imageGenerator()
    ctx.putImageData(imageData, 0, 0)

    return canvas
  }

  async function handleFile() {
    if (!file) {
      if (layer.current) {
        map.removeLayer(layer.current)
        layer.current = null
        onReady?.(false)
      }
      return
    }

    try {
      const tiff = await fromBlob(file)
      const overview = await getOverviewImage(tiff)
      const principalImage = await tiff.getImage()

      const { topLeft, topRight, bottomLeft } = await getCornerPoints(
        principalImage,
      )

      const samplesPerPixel = principalImage.getSamplesPerPixel()
      const isYCBCR =
        principalImage.fileDirectory?.PhotometricInterpretation === 6

      const imageCanvas = await drawImage(overview, isYCBCR, samplesPerPixel)

      if (isMounted()) {
        const imageLayer = L.imageOverlay.rotated(
          imageCanvas,
          topLeft,
          topRight,
          bottomLeft,
          { pmIgnore: true },
        )
        if (layer.current) {
          map.removeLayer(layer.current)
        }
        layer.current = imageLayer
        imageLayer.addTo(map)
        onReady?.()
      }
    } catch (err) {
      // TODO: adicionar tratamento de erro
      if (err instanceof GeoTiffError) {
        console.error(err)
      }

      // TODO: enviar para monitoramento
      console.error(err)
      if (isMounted()) {
        onError?.()
      }
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleFile()
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [file])

  useEffect(() => {
    if (layer.current) {
      if (isShow) {
        layer.current.setOpacity(1)
      } else {
        layer.current.setOpacity(0)
      }
    }
  }, [isShow])

  useEffect(() => {
    return () => {
      if (layer.current) {
        map.removeLayer(layer.current)
        layer.current = null
      }
    }
  }, [])

  return null
}
